/**
 * useVisualViewportVars.ts
 *
 * Publishes the visual viewport's height and vertical offset as CSS custom
 * properties on the document element, so a `position: fixed` overlay can pin
 * itself to the region the user can actually see while the on-screen keyboard is
 * open.
 *
 * ── Why this is needed at all ───────────────────────────────────────────────────
 * The keyboard does not shrink the *layout* viewport, and `fixed` elements are laid
 * out against the layout viewport. So a full-height panel keeps its pre-keyboard
 * height while the *visual* viewport shrinks and slides down inside it, and the user
 * ends up looking at a window onto the middle of the panel — header above the visible
 * band, input bar below it.
 *
 * `dvh` does not fix this. `dvh` tracks collapsing browser chrome, not the keyboard;
 * `docs/mobile-responsive-migration-log.md` already records that trap at chunk P6
 * ("the footer sat 137px below the fold"). A visualViewport listener is the only
 * thing that does, and it is the only approach that works on iOS Safari, which does
 * not support the `interactive-widget` viewport-meta key.
 *
 * ── The three properties, and why the inset is the one to reach for ─────────────
 * `--vv-height` and `--vv-offset-top` describe where the browser MOVED the visible
 * band to. A surface built on them repositions itself onto that band — and that only
 * lands correctly if `position: fixed` resolves against the layout viewport, which is
 * what the spec and the visual-viewport explainer say but is not what every iOS state
 * does. Get it backwards and the same declaration displaces the surface the other
 * way. SCRUM-1363/1364/1366 were re-opened three times on exactly that coin flip and
 * no device measurement was ever taken to settle it.
 *
 * `--vv-keyboard-inset` is the third property and the one new surfaces should use. It
 * is how many pixels at the BOTTOM of the layout viewport the keyboard occupies:
 *
 *     max(0, innerHeight - visualViewport.height - visualViewport.offsetTop)
 *
 * Paired with `top: 0` it pins the top edge and moves only the bottom edge, which
 * changes the problem in two ways worth understanding before touching this again:
 *
 *   - The top edge becomes a constant, so nothing can push the surface up. That is
 *     the reported defect, stated as an invariant a test can hold.
 *   - The composer never ends up under the keyboard, so the browser has no reason to
 *     scroll or offset anything, so `offsetTop` stays 0 — and the anchoring question
 *     above stops being load-bearing instead of having to be answered.
 *
 * The older two properties are unchanged and still correct for their consumers
 * (`Modal`, `sheet`, the conversation pages, `QaCaseEditModal`). Do not migrate them
 * as a drive-by; each is a surface with its own geometry and its own tests.
 *
 * ── Why CSS custom properties and not React state ───────────────────────────────
 * These events fire many times a second through the keyboard's open animation. React
 * state would re-render the consuming tree on every frame — for the AI assistant that
 * is the whole message list and every rendered markdown block. Writing a custom
 * property re-runs style resolution and nothing else: zero React renders.
 *
 * ── Why there is no horizontal term ─────────────────────────────────────────────
 * `offsetLeft` and `width` only diverge under zoom. The one zoom this app was actually
 * hitting — iOS auto-zooming on focus of a sub-16px control — is fixed at the source by
 * the auto-zoom floor at the bottom of `index.css` (SCRUM-1373), so it never reaches
 * this hook. What is left is the user's own pinch-zoom, and moving the UI out from
 * under someone who deliberately zoomed in is user-hostile. Do not add one.
 *
 * Pair with the `.h-visual-viewport` / `.top-visual-viewport` utilities in
 * `index.css`, which carry the `100dvh` / `0px` fallbacks for every case where this
 * hook writes nothing.
 */
import { useEffect } from 'react';
import { isDesktopLayoutForced } from './useBreakpoint';

/**
 * The custom properties this hook owns. Exported so tests and the CSS-side utilities
 * cannot drift apart on a rename.
 */
export const VISUAL_VIEWPORT_VARS = {
  height: '--vv-height',
  offsetTop: '--vv-offset-top',
  keyboardInset: '--vv-keyboard-inset',
} as const;

/**
 * How many enabled consumers are currently mounted.
 *
 * These properties live on one shared node — `document.documentElement` — but the hook
 * has several concurrent callers: the AI overlay, the shared modal's phone sheet, and
 * both conversation pages. More than one is routinely open at once, because the modal
 * is reachable from inside the others (`pages/messages/MessageMergeDialog.tsx` and
 * `v5/components/PrintOptionsDialog.tsx` are both `Modal`s rendered on a surface that
 * already enables this hook).
 *
 * Without a count, the first consumer to unmount removes the properties out from under
 * every consumer still open, and their effects do not re-run — `enabled` has not
 * changed for them — so nothing puts the properties back. The survivors silently fall
 * back to the `var(--vv-height, 100dvh)` default, which is the exact defect this hook
 * exists to fix: a composer sliding back under the keyboard, on close of an unrelated
 * dialog.
 *
 * Module scope is correct here rather than a context: the properties are global state
 * on a global node, so the count has to have the same lifetime and the same singleton
 * behaviour they do. Writes stay unsynchronised on purpose — every consumer writes the
 * same two values read from the same `window.visualViewport`, so a redundant write is
 * a no-op, and the surviving consumers' own listeners keep the values current.
 */
let activeConsumers = 0;

/**
 * Track the visual viewport while `enabled`, publishing it as CSS custom properties.
 *
 * Writes nothing — and attaches no listener — when disabled, when there is no DOM,
 * when the browser has no visualViewport API (this includes jsdom, so the hook is
 * inert in tests unless a suite stubs it), or when the KTD14 desktop-layout kill
 * switch is set. In every one of those cases the CSS fallbacks apply instead.
 */
export function useVisualViewportVars(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    if (isDesktopLayoutForced()) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const viewport = window.visualViewport;
    if (!viewport) return;

    const root = document.documentElement;
    let frame = 0;
    activeConsumers += 1;

    const write = () => {
      frame = 0;
      root.style.setProperty(VISUAL_VIEWPORT_VARS.height, `${viewport.height}px`);
      root.style.setProperty(VISUAL_VIEWPORT_VARS.offsetTop, `${viewport.offsetTop}px`);

      // `innerHeight` is re-read every write, not captured: it changes on rotation and
      // when the browser's own chrome collapses, and a stale value would offset the
      // bottom edge of every surface reading this by exactly that difference.
      // Clamped at 0 because both terms are the browser's to report — a transient
      // frame mid-animation can sum to more than the layout viewport, and a negative
      // `bottom` would pull a surface DOWN past the fold, which is the defect this
      // variable exists to prevent.
      const keyboardInset = Math.max(
        0,
        window.innerHeight - viewport.height - viewport.offsetTop,
      );
      root.style.setProperty(VISUAL_VIEWPORT_VARS.keyboardInset, `${keyboardInset}px`);
    };

    // Coalesce the burst of events the keyboard animation produces into one write
    // per frame. A pending frame is cancelled on cleanup so a write can never land
    // after the properties have been removed.
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(write);
    };

    // Write once synchronously, so the panel is correct on the frame it opens rather
    // than after the first event.
    write();

    // Both events matter. iOS reports the keyboard's shift of the visual viewport as
    // a `scroll`, not a `resize` — listening only for `resize` catches the height
    // change and misses the offset, which leaves the panel the right size in the
    // wrong place.
    viewport.addEventListener('resize', schedule);
    viewport.addEventListener('scroll', schedule);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      viewport.removeEventListener('resize', schedule);
      viewport.removeEventListener('scroll', schedule);

      activeConsumers -= 1;
      // Only the last consumer out clears the properties. Removing them while another
      // overlay is still open would drop that overlay onto the `100dvh` fallback with
      // no event pending to correct it — see the note on `activeConsumers`.
      if (activeConsumers > 0) return;

      // Remove rather than blank, so the `var()` fallbacks in the utilities take over
      // again on close and on unmount.
      root.style.removeProperty(VISUAL_VIEWPORT_VARS.height);
      root.style.removeProperty(VISUAL_VIEWPORT_VARS.offsetTop);
      root.style.removeProperty(VISUAL_VIEWPORT_VARS.keyboardInset);
    };
  }, [enabled]);
}
